/**
 * DischargedLoansRouter - the element behind /reports/discharged-loans.
 *
 * ONE route, ONE slug, ONE guard. A per-tenant tri-state flag decides which
 * PAGE renders inside it:
 *
 *   flag absent / 'off'   the standard DischargedLoansPage, byte-for-byte what
 *                         the other eight tenants see today.
 *   'observe'             the standard page, with the watch-list reachable at
 *                         ?view=watchlist for a soak before enablement.
 *   'enforce'             the watch-list, with the standard page still reachable
 *                         at ?view=standard.
 *
 * The flag is read as a DIRECT string off the company doc's featureFlags map,
 * the same way TeamManagementSwitch reads its own: there is no registry row, no
 * tier fallback and no hardcoded company allowlist. The backend vocabulary is
 * `off | observe | enforce` (services/client-functions/src/queries/shared/
 * resolveLoanBookFlags.ts) and is deliberately NOT the reportRegistry
 * `triStateFlag` vocabulary, whose enabled value is 'enabled'; the two are
 * different mechanisms and conflating them would gate a menu entry rather than
 * a page. Anything unrecognised falls back to the standard page: an unreadable
 * flag must never take a tenant somewhere new.
 *
 * The watch-list is React.lazy so the eight tenants on the standard page never
 * download its bundle. AppLayout already gates <Outlet> on the company load, so
 * companyData is non-null by the time this mounts; the explicit guard is
 * belt-and-braces against a future loading-state regression, and it renders the
 * standard page rather than a spinner because that is the flag-off answer.
 */

import React, { Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import DischargedLoansPage from './DischargedLoansPage';
import { useAppContext } from '../../components/layout/AppLayout';
import { SkeletonReportPage } from '../../components/common/SkeletonLoader';

// Code-split: tenants on the standard page never pay for this bundle.
const DischargeWatchlistPage = React.lazy(() => import('./DischargeWatchlistPage'));

export type DischargeWatchlistView = 'standard' | 'watchlist';

/**
 * PURE. Exported so the decision can be tested without a router, a page, or a
 * company document.
 */
export function resolveDischargeWatchlistView(
  featureFlags: Record<string, unknown> | undefined,
  search: URLSearchParams,
): DischargeWatchlistView {
  const mode = featureFlags?.discharge_watchlist;
  const view = search.get('view');
  if (mode === 'enforce') {
    return view === 'standard' ? 'standard' : 'watchlist';
  }
  if (mode === 'observe' && view === 'watchlist') {
    return 'watchlist';
  }
  return 'standard';
}

const DischargedLoansRouter: React.FC = () => {
  // Read through the optional chain the same way the same-dir routers do: the
  // outlet context is guaranteed by AppLayout today, and a future regression
  // there must land on the standard page, not on a crash.
  const ctx = useAppContext() as { companyData?: { featureFlags?: Record<string, unknown> } | null };
  const [search] = useSearchParams();

  const view = resolveDischargeWatchlistView(ctx?.companyData?.featureFlags, search);

  if (view === 'standard') {
    return <DischargedLoansPage />;
  }

  return (
    <Suspense fallback={<SkeletonReportPage statCards={6} statColumns={3} tableColumns={8} />}>
      <DischargeWatchlistPage />
    </Suspense>
  );
};

export default DischargedLoansRouter;
